// contactApiActions.js
import axios from 'axios';
import { BASE_URL } from '../../../../Constants/constants';
import { prepareContactFormData } from './ContactFormUtils';

/**
 * Creates authorization configuration object for API requests
 * @param {boolean} isMultipart - Whether the request contains multipart form data
 * @returns {Object} Configuration object with authorization headers
 */
const getAuthConfig = (isMultipart = false) => ({
    headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        ...(isMultipart ? { 'Content-Type': 'multipart/form-data' } : {})
    }
});

/**
 * Fetches contact data for the current user
 * @returns {Promise<Object|null>} Contact data or null if not found
 * @throws {Error} If the request fails for reasons other than 404
 */
export const fetchContactData = async () => {
    try {
        const response = await axios.get(`${BASE_URL}/contact/me`, getAuthConfig());
        return response.data;
    } catch (error) {
        if (error.response?.status === 404) {
            return null;
        }
        console.error('Error fetching contact data:', error);
        throw new Error('Failed to fetch contact information.');
    }
};

/**
 * Saves or updates contact data for the current user
 * @param {Object} contact - Contact data to save
 * @param {Object} currentUser - Current user information
 * @returns {Promise<Object>} Saved contact data
 * @throws {Error} If the save operation fails
 */
export const saveContactData = async (contact, currentUser) => {
    try {
        const formData = prepareContactFormData(contact, currentUser);

        const url = contact.id
            ? `${BASE_URL}/contact/${contact.id}`
            : `${BASE_URL}/contact/`;

        const response = await axios({
            method: contact.id ? 'put' : 'post',
            url,
            data: formData,
            ...getAuthConfig(true)
        });

        return response.data;
    } catch (error) {
        console.error('Error saving contact data:', error);
        throw new Error('Failed to save contact information.');
    }
};

/**
 * Deletes contact data for the specified ID
 * @param {string|number} contactId - ID of the contact to delete
 * @returns {Promise<Object>} Response data from the delete operation
 * @throws {Error} If the delete operation fails
 */
export const deleteContactData = async (contactId) => {
    try {
        const response = await axios.delete(
            `${BASE_URL}/contact/${contactId}`,
            getAuthConfig()
        );
        return response.data;
    } catch (error) {
        console.error('Error deleting contact data:', error);
        throw new Error('Failed to delete contact information.');
    }
};